import { useState, type ReactNode } from 'react'
import type { SelectorParam } from 'i18next'
import { Download, EllipsisVertical, Share, SquarePlus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/Button'
import { useInstallPrompt } from '@/hooks/useInstallPrompt'
import {
  getMobilePlatform,
  isStandalone,
  loadInstallDismissed,
  saveInstallDismissed,
  type MobilePlatform,
} from '@/lib/pwa'
import './InstallPrompt.css'

// Подсказка «добавьте podpipe на главный экран». Показывается один раз, только
// в мобильном браузере (не в установленном standalone-PWA) и пока не закрыта.
// Платформа фиксируется при первом рендере — на десктопе компонент сразу no-op.
const PLATFORM = getMobilePlatform()

interface Step {
  icon: ReactNode
  // Селектор, а не текст: перевод — при рендере, иначе язык замёрз бы на импорте модуля.
  text: SelectorParam
}

function Steps({ steps }: { steps: Step[] }) {
  const { t } = useTranslation()
  return (
    <ol className="install-steps">
      {steps.map((step, i) => (
        <li key={i} className="install-step">
          <span className="install-step__icon" aria-hidden>
            {step.icon}
          </span>
          <span className="install-step__text">{t(step.text)}</span>
        </li>
      ))}
    </ol>
  )
}

const IOS_STEPS: Step[] = [
  { icon: <Share />, text: ($) => $.install.iosShare },
  { icon: <SquarePlus />, text: ($) => $.install.iosAddToHome },
]

const ANDROID_STEPS: Step[] = [
  { icon: <EllipsisVertical />, text: ($) => $.install.androidMenu },
  { icon: <Download />, text: ($) => $.install.androidInstall },
]

function PromptBody({ platform, onClose }: { platform: MobilePlatform; onClose: () => void }) {
  const { t } = useTranslation()
  const { canPromptInstall, promptInstall } = useInstallPrompt()

  if (platform === 'ios') {
    return (
      <>
        <p className="dialog-text">{t(($) => $.install.description)}</p>
        <Steps steps={IOS_STEPS} />
        <p className="dialog-text dialog-text--muted">{t(($) => $.install.safariOnly)}</p>
      </>
    )
  }

  // Android: если доступен нативный запрос — кнопка в один тап; инструкция через
  // меню остаётся ниже как запасной путь (актуален для прочих браузеров).
  const handleInstall = async () => {
    const outcome = await promptInstall()
    if (outcome === 'accepted') onClose()
  }

  return (
    <>
      <p className="dialog-text">{t(($) => $.install.description)}</p>
      {canPromptInstall ? (
        <div className="dialog-actions">
          <Button variant="primary" fullWidth onClick={handleInstall}>
            {t(($) => $.install.install)}
          </Button>
        </div>
      ) : (
        <Steps steps={ANDROID_STEPS} />
      )}
    </>
  )
}

export function InstallPrompt() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(
    () => PLATFORM !== null && !isStandalone() && !loadInstallDismissed(),
  )

  if (PLATFORM === null) return null

  // Закрытие (× / ESC / бэкдроп / успешная установка) — запоминаем навсегда.
  const handleClose = () => {
    saveInstallDismissed()
    setOpen(false)
  }

  return (
    <Modal open={open} onClose={handleClose} title={t(($) => $.install.title)} width={420}>
      <PromptBody platform={PLATFORM} onClose={handleClose} />
    </Modal>
  )
}
